import * as Sentry from "@sentry/react";
import { Condition, ConditionTypes } from "@types-runway/condition";
import { RunwayPrice } from "@types-runway/state";

// Known exception to the utils layer rule: this module calls the API (see src/utils/CLAUDE.md).
// eslint-disable-next-line no-restricted-imports
import { condition as conditionApi, stripe } from "@api/index";
import { calculateMalariaPricing } from "@utils/medication/malariaPricing";

interface MappedCondition extends Condition {
  priority?: number;
}

export const mapConditionPrescriptions = async (
  conditions: MappedCondition[],
  travelDays: number,
  medicationPriority?: number | Map<string, number>
): Promise<Condition[]> => {
  const pricesIds = conditions.map((condition: Condition) => {
    return condition.product.metadata?.medication || condition.product.metadata?.medication_1;
  });

  let prescriptionPrices: RunwayPrice[] = [];

  try {
    const { data } = await stripe.stripePost("prices", {
      pricesIds,
    });
    prescriptionPrices = data;
  } catch (err) {
    console.log(err);
    return [];
  }

  const mappedConditions = await Promise.all(
    conditions.map(async (condition: Condition, index: number) => {
      const prescriptionId =
        condition.product.metadata?.medication || condition.product.metadata?.medication_1;
      const pricing = prescriptionPrices.find(
        (prescriptionPrice) => prescriptionPrice.id === prescriptionId
      );
      const metadata = pricing?.product?.metadata;
      let prescriptionCostAmount = pricing?.unit_amount || 0;
      let mdiId = metadata?.MDI_ID;
      let quantity = parseInt(metadata?.quantity ?? "0");

      // Resolve medication before pricing — pricing model depends on medication type.
      let medication = condition.suggestedMedication;
      if (!condition.suggestedMedication?.name) {
        const itemPriority =
          medicationPriority instanceof Map
            ? medicationPriority.get(condition.product.name)
            : index === 0
              ? medicationPriority
              : undefined;

        medication = await enrichConditionWithMedication(condition, itemPriority);
      }

      // The server resolves MDI_ID from the correct Stripe medication product and sets
      // it as medication.id — trust that value over the medication_1 price fallback,
      // which is always wrong for non-primary medications (e.g. ARAKODA = medication_3).
      mdiId = medication?.id || mdiId;

      if (condition.product.name === ConditionTypes.MALARIA_CONSULTATION) {
        const pricing = calculateMalariaPricing(
          medication?.name ?? "",
          travelDays,
          prescriptionCostAmount,
          quantity
        );
        prescriptionCostAmount = pricing.price;
        quantity = pricing.quantity;
      }

      return {
        ...condition,
        suggestedMedication: {
          ...medication,
          quantity,
          id: mdiId,
        },
        price: prescriptionCostAmount || 0,
      };
    })
  );
  return mappedConditions;
};

export const enrichConditionWithMedication = async (condition: Condition, priority?: number) => {
  try {
    const {
      data: { medication },
    } = await conditionApi.conditionGet(
      `/${condition.product.name}/medication${priority ? `?priority=${priority}` : ""}`
    );
    return medication;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};
